import { useState } from "react";
import { Receipt, TrendingDown, TrendingUp, Truck } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { SalesSummaryDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";

type SalesSummaryCardsProps = {
  summary?: SalesSummaryDTO;
  isLoading?: boolean;
  /** On mobile (<sm), collapse the 4th (return) card behind a "ເບິ່ງເພີ່ມ" button. Default true. */
  collapsibleExtra?: boolean;
};

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  accentClass,
}: {
  icon: typeof Receipt;
  label: string;
  value: string;
  sub?: string;
  accentClass: string;
}) {
  return (
    <div className="brand-card flex items-center gap-3 p-4">
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${accentClass}`}
      >
        <Icon />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span className="font-display text-xl tabular-nums leading-tight sm:text-2xl">
          {value}
        </span>
        {sub ? (
          <span className="text-muted-foreground text-xs">{sub}</span>
        ) : null}
      </div>
    </div>
  );
}

function SalesConfirmedBreakdown({
  confirmed,
}: {
  confirmed: SalesSummaryDTO["confirmed"];
}) {
  const discountNum = Number(confirmed.discountAmount);
  const shippingNum = Number(confirmed.shippingFee);

  return (
    <div className="brand-card grid gap-3 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">ຍອດສິນຄ້າ</span>
        <span className="font-medium tabular-nums">
          {formatLak(confirmed.subtotal)}
        </span>
      </div>
      {discountNum > 0 ? (
        <div className="flex flex-col gap-0.5">
          <span className="text-muted-foreground text-xs">ສ່ວນລົດ</span>
          <span className="font-medium text-destructive tabular-nums">
            −{formatLak(confirmed.discountAmount)}
          </span>
        </div>
      ) : null}
      {shippingNum > 0 ? (
        <div className="flex flex-col gap-0.5">
          <span className="text-muted-foreground text-xs">ຄ່າສົ່ງ</span>
          <span className="font-medium tabular-nums">
            {formatLak(confirmed.shippingFee)}
          </span>
        </div>
      ) : null}
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">ລວມທັງໝົດ</span>
        <span className="font-display text-lg text-secondary tabular-nums">
          {formatLak(confirmed.revenue)}
        </span>
      </div>
    </div>
  );
}

export function SalesSummaryCards({
  summary,
  isLoading,
  collapsibleExtra = true,
}: SalesSummaryCardsProps) {
  const [showExtra, setShowExtra] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-22 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-20 rounded-2xl" />
      </div>
    );
  }

  const confirmed = summary?.confirmed;
  const prepaid = summary?.prepaid;
  const cod = summary?.cod;
  const returned = summary?.returned;
  const returnLossNum = Number(returned?.returnShippingLoss ?? 0);

  const mobileHideExtra = collapsibleExtra && !showExtra;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={TrendingUp}
          label="ຍອດຂາຍ (ຢືນຢັນ)"
          value={formatLak(confirmed?.revenue ?? "0")}
          sub={`${confirmed?.orderCount ?? 0} ອໍເດີ້ · ສະເລ່ຍ ${formatLak(confirmed?.averageOrderValue ?? "0")}`}
          accentClass="bg-primary/20 text-secondary"
        />
        <StatCard
          icon={Receipt}
          label="ໂອນກ່ອນ"
          value={formatLak(prepaid?.revenue ?? "0")}
          sub={`${prepaid?.orderCount ?? 0} ອໍເດີ້`}
          accentClass="bg-secondary/20 text-secondary"
        />
        <StatCard
          icon={Truck}
          label="COD"
          value={formatLak(cod?.revenue ?? "0")}
          sub={`${cod?.orderCount ?? 0} ອໍເດີ້`}
          accentClass="bg-accent/40 text-foreground"
        />
        {/* 4th card: always visible on sm+, collapsed on mobile when collapsibleExtra */}
        <div className={mobileHideExtra ? "hidden sm:block" : undefined}>
          <StatCard
            icon={TrendingDown}
            label="ຕີກັບ / ຄືນ"
            value={formatLak(returned?.revenue ?? "0")}
            sub={
              returnLossNum > 0
                ? `${returned?.orderCount ?? 0} ອໍເດີ້ · ຄ່າສົ່ງເສຍ ${formatLak(returned?.returnShippingLoss ?? "0")}`
                : `${returned?.orderCount ?? 0} ອໍເດີ້`
            }
            accentClass="bg-destructive/15 text-destructive"
          />
        </div>
      </div>
      {mobileHideExtra ? (
        <button
          type="button"
          onClick={() => setShowExtra(true)}
          className="self-start rounded-full border border-border bg-background px-3 py-1.5 font-medium text-muted-foreground text-xs hover:bg-muted/60 sm:hidden"
        >
          ເບິ່ງເພີ່ມ (ຕີກັບ / ຄືນ)
        </button>
      ) : null}
      {confirmed && confirmed.orderCount > 0 ? (
        <SalesConfirmedBreakdown confirmed={confirmed} />
      ) : null}
    </div>
  );
}
